import { useEffect, useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";

const ease = [0.76, 0, 0.24, 1];

const Line = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-[0.18em] -mb-[0.18em]">
    <motion.span
      className={`block ${className || ""}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onJoin }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yArt = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // cursor-tracking parallax on the emblem (spring-eased)
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(mx, { stiffness: 60, damping: 18, mass: 0.6 });
  const py = useSpring(my, { stiffness: 60, damping: 18, mass: 0.6 });

  useEffect(() => {
    const handle = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // range: -18 .. +18 px based on distance from center
      mx.set(((e.clientX - w / 2) / w) * 36);
      my.set(((e.clientY - h / 2) / h) * 36);
    };
    window.addEventListener("mousemove", handle);
    return () => window.removeEventListener("mousemove", handle);
  }, [mx, my]);

  return (
    <section id="top" ref={ref} data-testid="hero" className="relative min-h-screen w-full overflow-hidden">
      {/* faint counter-rotating ambient emblem — deep background */}
      <motion.img
        src="/emblem_glow.png?v=4"
        alt=""
        aria-hidden
        animate={{ rotate: -360 }}
        transition={{ duration: 180, repeat: Infinity, ease: "linear" }}
        className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[130vw] md:w-[80vw] max-w-[1100px] opacity-[0.06] z-0"
      />

      {/* hero emblem — cinematic focal element on the right */}
      <motion.div
        style={{ y: yArt }}
        className="pointer-events-none absolute right-[-6%] md:right-[6%] top-[10%] md:top-[10%] w-[70%] sm:w-[50%] md:w-[36%] max-w-[520px] z-10"
      >
        <motion.div style={{ x: px, y: py }}>
        <motion.div
          initial={{ opacity: 0, scale: 1.15, filter: "blur(20px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.8, delay: 0.9, ease }}
          className="relative aspect-square"
        >
          {/* soft radial halo */}
          <div
            className="absolute inset-[-20%] rounded-full blur-3xl"
            style={{
              background:
                "radial-gradient(circle at center, rgba(139,58,43,0.42) 0%, rgba(139,58,43,0.14) 35%, transparent 70%)",
            }}
          />

          {/* editorial crosshair / registration marks */}
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 w-px h-8 bg-bds-fg/25" />
          <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-px h-8 bg-bds-fg/25" />
          <span className="absolute top-1/2 -left-3 -translate-y-1/2 h-px w-8 bg-bds-fg/25" />
          <span className="absolute top-1/2 -right-3 -translate-y-1/2 h-px w-8 bg-bds-fg/25" />
          <span className="absolute -top-6 left-0 font-mono-bds text-[10px] tracking-[0.3em] text-bds-fg/45">01 · EMBLEM</span>
          <span className="absolute -bottom-6 right-0 font-mono-bds text-[10px] tracking-[0.3em] text-bds-fg/45">BDS · NZ</span>

          {/* symbol — slowly rotating (wrapper), gently breathing (inner) */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            className="absolute inset-[8%]"
          >
            <motion.div
              animate={{ scale: [1, 1.03, 1] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="w-full h-full"
            >
              <img
                src="/emblem_glow.png?v=4"
                alt="Black Dog Society emblem"
                draggable={false}
                style={{ filter: "drop-shadow(0 0 40px rgba(139,58,43,0.5))" }}
                className="w-full h-full object-contain select-none"
              />
            </motion.div>
          </motion.div>
        </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-20 mx-auto max-w-[1600px] px-6 md:px-12 min-h-screen flex flex-col justify-center pt-24"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="font-mono-bds text-[10px] md:text-xs uppercase tracking-[0.35em] text-bds-muted mb-8"
        >
          Wellington, New Zealand — Chapter I · 26.11.2026
        </motion.p>

        <h1 className="font-serif-display font-light tracking-tighter leading-[0.82] text-[18vw] md:text-[13vw] lg:text-[11rem]">
          <Line delay={0.55}>Black Dog</Line>
          <Line delay={0.7} className="italic text-bds-muted">Society</Line>
        </h1>

        <div className="mt-10 md:mt-14 max-w-md md:ml-2">
          <Line delay={1.05} className="font-grotesk text-base md:text-lg text-bds-fg/80 leading-relaxed">
            Face your demons.
          </Line>
          <Line delay={1.15} className="font-grotesk text-base md:text-lg text-bds-fg/80 leading-relaxed">
            Wear your story.
          </Line>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="mt-9 flex items-center gap-6"
          >
            <button
              onClick={onJoin}
              data-testid="hero-join-btn"
              className="font-mono-bds text-xs uppercase tracking-[0.2em] px-8 py-4 bg-bds-fg text-bds-bg hover:bg-bds-accent hover:text-bds-cream transition-colors duration-300"
            >
              Enter the Society
            </button>
            <a href="/chapter-i" data-testid="hero-chapter-link" className="font-mono-bds text-xs uppercase tracking-[0.2em] text-bds-muted hover:text-bds-fg transition-colors border-b border-transparent hover:border-bds-fg pb-1">
              Chapter I
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3"
      >
        <span className="font-mono-bds text-[10px] uppercase tracking-[0.3em] text-bds-muted">Scroll</span>
        <motion.span
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block w-px h-10 bg-gradient-to-b from-bds-muted to-transparent"
        />
      </motion.div>
    </section>
  );
};
